// ============================================
// DTK S.r.l. - Types & Interfaces
// Arricchite dal DB legacy MySQL (dtk_mysql_export.sql)
// ============================================

// --- Lookup Tables ---

export interface FamigliaArticoli {
  id: number;            // IdFamigliaArticolo
  dtkCodice: string;     // DTKCodice
  descrizione: string;   // Descrizione
  stato: number;         // stato (1=attivo)
  immagine?: string;
  codReport?: string;
}

export interface CategoriaMerceologica {
  id: number;            // IdCategoriaMerceologica
  dtkCodice: string;     // DTKCodice
  descrizione: string;   // Descrizione
}

export interface Marca {
  id: number;            // IdMarca
  dtkCodice: string;     // DTKCodice
  nomeMarca: string;     // NomeMarca
  immagine?: string;
  stato: number;         // 1=attivo, 0=nascosto
}

export interface Colore {
  id: number;
  dtkCodice: string;
  descrizione: string;
}

export interface UnitaDiMisura {
  id: number;
  codice: string;        // es. 'PZ.', 'CF.'
  descrizione: string;
}

export interface TipoMacchina {
  id: number;            // IdTipoMacchina
  dtkCodice: string;
  descrizione: string;   // es. 'MFC/Stampanti Laser'
}

export interface Macchina {
  id: number;            // IdMacchina
  idTipoMacchina: number;
  tipoMacchina?: string; // nome tipo
  idMarca: number;
  nomeMarca: string;
  descrizione: string;
  descrizioneSearch?: string;
}

export interface AbbinamentiArticolo {
  idAbbinamento: number;
  codArticolo: string;   // FK → Product.sku
  idMacchina: number;
  nomeMacchina?: string;
  nomeMarca?: string;
  codiceOem: string;     // Codice OEM produttore macchina
  descrizione?: string;
  dataAbbinamento?: string;
}

export interface Listino {
  id: number;            // IdListino
  dtkCodice: string;     // es. 'UR', 'UR2'
  descrizione: string;   // es. 'LISTINO RIVENDITORI 1'
  idValuta: number;
  valuta: string;        // es. 'EUR'
  isNettoIva: boolean;
  dataInizioValidita: string;
  dataFineValidita: string;
}

export interface LineeListino {
  id: number;
  idListino: number;
  idArticolo: number;
  codArticolo: string;
  prezzoUnitario: number;
}

export interface CondizioniPagamento {
  id: number;            // idcondizione_pagamento
  dtkCodice: string;     // es. 'BFM3', 'RID30'
  descrizione: string;   // es. 'BON. BANCARIO 30 GG. F.M.'
  stato: number;
  eliminato: boolean;
}

export interface ModalitaSpedizione {
  id: number;            // IdModalitaSpedizione
  descrizione: string;   // es. 'Corriere Espresso'
  codiceDtk?: string;    // es. 'COR', 'MIT'
}

export interface StatoOrdine {
  id: number;            // IdStatoOrdine
  descrizione: string;   // es. 'Creato', 'Acquisito', 'Parzialmente evaso', 'Evaso'
}

export interface TipoOrdine {
  id: number;            // IdTipoOrdine
  nomeTipo: string;      // es. 'Web', 'Adhoc', 'Da preventivo', 'Telefonico'
}

// --- Pricing ---

export interface PrezzoTipico {
  idArticolo: number;
  idCliente: number;
  codArticolo: string;
  codCliente: string;
  prezzo: number;
  quantita?: number;     // soglia quantità (opzionale)
}

export interface ScontoArticoloCliente {
  idArticolo: number;
  idCliente: number;
  codArticolo: string;
  codCliente: string;
  sconto: number;        // percentuale es. 10.5
}

export interface Promozione {
  id: number;            // IdPromozione
  idTipologiaPromozione: number;
  tipologia: string;     // es. 'Offerta', 'Bundle', 'Flash Sale'
  idArticolo: number;
  codArticolo: string;
  prezzo?: number;       // prezzo promo (alternativo a sconto)
  sconto?: number;       // % di sconto (alternativo a prezzo)
  quantitaMinima?: number;
  dataInizioValidita: string;
  dataFineValidita: string;
  attiva: boolean;
}

export interface ScontoScatola {
  idArticolo: number;
  codArticolo: string;
  quantitaMinima: number;
  sconto: number;        // % es. 5, 10, 15
}

// --- Products ---

/** Singolo componente di un kit (articolo composto) */
export interface KitItem {
  sku: string;
  qty: number;
  label: string;
  um?: string;  // unità di misura (es. 'PZ.')
}

/** Allegato PDF di un articolo (scheda tecnica, SDS, manuale). ``url`` è
 *  relativo al backend; con ``?download=1`` il file si scarica invece di
 *  aprirsi nel visualizzatore del browser. */
export interface ProductAttachment {
  id: string;
  name: string;
  filename: string;
  url: string;          // URL pubblico del bucket: apre il PDF nel browser
  downloadUrl: string;  // URL firmato che lo scarica col nome originale
  size: number;
  uploadedAt: string;
}

export interface Product {
  // === Campi base originali ===
  sku: string;           // CodiceArticolo / DTKCodice
  brand: string;         // NomeMarca
  category: string;      // id categoria frontend (toner, laser-inkjet, opc-drum, ricambi)
  title: string;         // Descrizione
  compatibility: string; // Macchine compatibili (testo)
  type: string;          // Compatibile / Originale / Kit / Rigenerato
  yield: string;         // Resa (es. '3.000 pagine')
  color: string;         // Colore cartuccia

  // === Lookup NTS (decodifica codici → label umane) ===
  erp?: {
    detail?: {
      brand?: { code?: number | string; label?: string };
      famigliaProdotto?: { code?: number | string; label?: string };
      gruppo?: { code?: number | string; label?: string };
      sottogruppo?: { code?: number | string; label?: string };
      colore?: { code?: number | string; label?: string } | null;
    };
  };

  // === Campi DB legacy (dbo_Articoli) ===
  dtkCodice?: string;                    // DTKCodice (es. '1054027')
  idFamiglia?: number;                   // FK → FamiglieArticoli
  famiglia?: string;                     // Descrizione famiglia
  idCategoriaMerceologica?: number;      // FK → CategorieMerceologiche
  categoriaMerceologica?: string;        // Descrizione categoria
  idMarca?: number;                      // FK → Marche
  idColore?: number;
  coloreDescrizione?: string;
  idUdm?: number;
  unitaDiMisura?: string;                // es. 'PZ.'

  // === Stock & Logistica ===
  minimoVendibile?: number;              // MinimoVendibile (es. 1, 2, 5)
  confezione?: number;                   // Pezzi per confezione
  richiedeAbbinamento?: boolean;         // RichiedeAbbinamento (serve modello macchina)
  isKit?: boolean;                       // Kit (articolo composto)
  kitItems?: KitItem[];                  // Composizione del kit
  tipoKit?: 'S' | 'A' | string;          // S=somma figli, A=prezzo padre (campo NTS ar_tipokit)
  originale?: boolean;                   // 0=compatibile, 1=originale
  pesante?: boolean;                     // Flag articolo pesante (sovrappeso)
  inEsaurimento?: boolean;               // ar_inesaur='S' → articolo in esaurimento stock
  prossimArrivo?: string | null;         // Data prossimo arrivo stock (ar_hhegdataarrivo)
  qtaArrivo?: number | null;             // Quantità in arrivo (ar_hhegqtaarrivo)
  images?: { path: string; url: string; isPrimary?: boolean; uploadedAt?: string }[]; // Foto reali (Mongo-only, Admin > Immagini)
  attachments?: ProductAttachment[];     // PDF scaricabili (Mongo-only, Admin > Allegati)
  lineaPromo?: 'PRO' | 'SMART' | string | null; // Linea promozionale NTS (ar_hhliton) — mostrata come badge
  livRibasso?: number | null;            // Livello ribasso NTS 0-4 (lc_hhlivribasso) — solo salvato, il badge usa `ribasso`
  prezzoOrig?: number | null;            // Prezzo pieno secondo NTS (lc_hhprezzoorig) — solo salvato
  /** Prezzo ridotto deciso dal portale (60 giorni dal calo, backend `erp/ribassi.py`). */
  ribasso?: { dal: string; fino: string; prezzoPrecedente?: number | null } | null;
  outlet?: boolean | null;               // Flag outlet NTS (lc_hhoutlet) — solo DB, nessun badge per ora

  // === Sconto Scatola (fonte ERP via ScontiApriElenco, so_hhegflscatola="S") ===
  boxQty?: number | null;                // Qtà pz per scatola intera (es. 6, 12)
  boxPrice?: number | null;              // Prezzo totale scatola intera (€, calcolato)
  boxDiscountPct?: number | null;        // % sconto scatola (da ERP so_scont1)
  boxDemo?: boolean | null;              // True se entry creata per demo (badge "DEMO")

  // === Offerta a tempo (fonte ERP via ScontiApriElenco, so_datscad≠2099) ===
  // Esposta dal backend solo se attiva e nella finestra di validità.
  promo?: {
    priceOverride: number;               // Prezzo finale promo (€, calcolato)
    discountPct?: number;                // % sconto originale da ERP
    startDate?: string | null;           // ISO 8601 UTC
    endDate?: string | null;             // ISO 8601 UTC
    isPermanent?: boolean;               // true = sconto permanente (no scadenza)
    label?: string;                      // Es. "-25%", "Promo Marzo"
    discountText?: string | null;        // Es. "-25%"
    source?: string;                     // "erp"
  } | null;

  // === Dimensioni & Peso ===
  pesoLordo?: number;                    // kg
  pesoNetto?: number;                    // kg
  lunghezza?: number;                    // cm
  larghezza?: number;                    // cm
  altezza?: number;                      // cm
  volume?: number;                       // m³

  // === Pricing (opzionali, usati dove disponibili) ===
  prezzoAcquisto?: number;               // Costo interno (non mostrato al cliente)
  classeSconto?: string;                 // Codice classe sconto (es. '109501')
  /** Prezzo di listino base (€). Popolato da NTS al sync articoli_dettaglio. */
  listino10?: number | null;
  /** True se il prezzo è stato generato come mock per la demo — verrà
   * sovrascritto dal valore reale appena ERP lo fornisce. */
  listino10IsMock?: boolean | null;

  // === Stock (da HhArtproApri — orario) ===
  stock?: number | null;

  // === Codici OEM (da HhOEMApri — giornaliero) ===
  oemCodes?: string[];
  /** Popolato solo dalla ricerca codeOnly (navbar): codice OEM specifico che ha fatto match con la query. */
  matchedOemCode?: string | null;

  // === Descrizioni web (da HhArtvalApri — giornaliero) ===
  descrWeb?: string | null;
  noteWeb?: string | null;

  // === Codici ===
  codiceBarcode?: string;                // CodiceABArre (EAN/Barcode)

  // === Descrizione estesa ===
  descrizione?: string;             // Descrizione lunga (scheda tecnica, note)

  // === Date / Novità ===
  dataInserimento?: string;
  dataUltimaModifica?: string;
  idTipologiaNovita?: number;
  novita?: string;                       // es. 'Nuovo Arrivo', 'Aggiornato'
  dataScadenzaNovita?: string;

  // === Stato ===

  // === Web visibility & arricchimento ERP ===
  webVisible?: boolean | null;   // True se il prodotto è visibile sul sito B2B (flag NTS, può essere override demo)
  webSellable?: boolean | null;  // True se il prodotto è vendibile online (flag NTS)
  rimossoDalGestionale?: boolean | null;  // Articolo rimosso dal gestionale: non si mostra né si vende
  webVisibleErp?: boolean | null;   // Valore autoritativo ar_webvis preso da erp.detail
  webSellableErp?: boolean | null;  // Valore autoritativo ar_webvend preso da erp.detail
  webVisibleIsDemo?: boolean | null; // True se webVisible è un override demo (non NTS)
  enriched?: boolean;            // True se erp.detail è stato popolato dal job articoli_dettaglio
  seedOnly?: boolean;            // True se è un prodotto seed (non esiste come ar_codart in NTS)
}

export interface CartItem extends Product {
  qty: number;
}

// --- Customers ---

export interface Indirizzo {
  id: number;            // IdIndirizzo
  idCliente: number;
  idTipologiaIndirizzo: number;
  tipologia: string;     // es. 'Sede Legale', 'Operativa', 'Spedizione'
  dtkCodice?: string;
  nominativo?: string;
  via?: string;
  civico?: string;
  cap?: string;
  citta?: string;
  provincia?: string;
  stato?: string;        // Nazione
  telefono?: string;
  cellulare?: string;
  fax?: string;
  email?: string;
  url?: string;
  isDefault?: boolean;
}

export interface FidoCliente {
  idCliente: number;
  fidoConcesso: number;
  fidoUsato: number;
  fidoDisponibile: number;
  dataAggiornamento: string;
}

export interface Customer {
  // === Identificativi ===
  id: string;            // IdCliente come stringa
  idCliente: number;     // IdCliente numerico
  dtkCodice: string;     // DTKCodice (es. '9010079')
  dtkCodiceOld?: string; // CodiceDtkOld
  userId?: string;       // UserId (GUID accesso portale)

  // === Anagrafica ===
  ragioneSociale: string;
  nome?: string;
  cognome?: string;
  partitaIva?: string;
  codiceFiscale?: string;
  codiceUnivoco?: string; // SDI per fatturazione elettronica

  // === Contatti ===
  email?: string;
  emailPec?: string;
  emailComunicazione?: string;
  emailComunicazione2?: string;
  emailComunicazione3?: string;
  emailRepartoTecnico?: string;
  emailRepartoAmministrativo?: string;
  emailRepartoAcquisti?: string;
  sceltaEmailComunicazione?: number; // 1,2,3
  telefono?: string;
  cellulare?: string;

  // === Classificazione Commerciale ===
  idAgente?: number;
  codAgente?: number;
  nomeAgente?: string;
  idListino?: number;
  codListino?: string;
  nomeListino?: string;
  idCondizionePagamento?: number;
  codCondizionePagamento?: string;
  descCondizionePagamento?: string;
  idTipoCliente?: number;
  tipoCliente?: string;  // es. 'Rivenditore', 'Privato', 'Azienda'
  idQualifica?: number;
  qualifica?: string;
  idTipoAzienda?: number;
  tipoAzienda?: string;
  idSettore?: number;
  settore?: string;
  codiceZona?: string;   // Zona commerciale (es. '9', '14')

  // === Marchi Trattati ===
  idMarchioTrattato1?: number;
  nomeMarchioTrattato1?: string;
  idMarchioTrattato2?: number;
  nomeMarchioTrattato2?: string;
  idMarchioTrattato3?: number;
  nomeMarchioTrattato3?: string;

  // === Stato & Date ===
  stato: number;         // 0=disattivo, 1=attivo
  eliminato: boolean;
  dataInserimento: string;
  dataUltimaModifica: string;
  dataDisattivazione?: string;
  contratti_vendita_da?: string;
  contratti_vendita_a?: string;

  // === Documenti allegati ===
  visuraCamerale?: string;
  iban?: string;
  pathFoto?: string;

  // === Indirizzi ===
  indirizzi?: Indirizzo[];

  // === Fido ===
  fido?: FidoCliente;
}

// --- Orders ---

export interface LineeOrdine {
  id: number;            // IdLineaOrdine
  idOrdine: number;
  idArticolo: number;
  sku: string;           // CodiceArticolo
  title: string;         // Descrizione al momento dell'ordine
  brand: string;
  category: string;
  idMarca?: number;
  nomeMarca?: string;
  codiceOem?: string;    // Codice OEM produttore macchina
  numLinea: number;
  quantita: number;
  quantitaEvasa: number;
  quantitaInArrivo?: number;
  prezzoUnitario: number;
  prezzoUnitarioConsigliato?: number; // Prezzo di listino
  percentualeSconto: number;          // % sconto applicato
  totaleLinea: number;
  dataPrevistaConsegna?: string;
  annullata: boolean;
  chiusa: boolean;
}

export interface IndirizzoSpedizioneOrdine {
  id: number;
  idOrdine?: number;
  nominativo?: string;
  via: string;
  civico?: string;
  cap: string;
  citta: string;
  provincia: string;
  stato?: string;
  telefono?: string;
  email?: string;
}

export interface Order {
  // === Identificativi ===
  id: string;            // IdOrdine come stringa
  idOrdine: number;      // IdOrdine numerico
  guid: string;          // GUID univoco ordine
  numeroOrdine: string;  // Formato 'ANNO-PROGRESSIVO' (es. '2026-147')

  // === Riferimenti ===
  idCliente: number;
  ragioneSocialeCliente: string;
  dtkCodiceCliente: string;
  idUser?: string;

  // === Stato & Tipo ===
  idStatoOrdine: number;
  statoOrdine: string;   // es. 'Creato', 'Acquisito', 'Parzialmente evaso', 'Evaso'
  idTipoOrdine: number;
  tipoOrdine: string;    // es. 'Web', 'Adhoc', 'Da preventivo'

  // === Spedizione & Pagamento ===
  idModalitaSpedizione: number;
  modalitaSpedizione: string;
  idCondizionePagamento: number;
  condizionePagamento: string;
  idIndirizzoSpedizione?: number;
  indirizzoSpedizione?: IndirizzoSpedizioneOrdine;

  // === Importi ===
  percentualeIva: number;  // es. 22
  imponibile: number;
  iva: number;
  totaleOrdine: number;
  speseSpedizione: number;
  importoSconto: number;

  // === Date ===
  dataOrdine: string;

  // === Info ===
  note?: string;
  emailRiferimento?: string;
  motivazioneRifiuto?: string;

  // === Stato sincronizzazione ===
  isEsportato: boolean;  // inviato al gestionale
  isEvaso: boolean;      // completamente evaso

  // === Tracking ===
  trackingCode?: string;
  trackingUrl?: string;
  carrier?: string;

  // === Righe ===
  items: LineeOrdine[];

  // === Campi compatibilità UI (vecchi) ===
  date: string;          // = dataOrdine
  status: 'confermato' | 'in_lavorazione' | 'spedito' | 'consegnato' | 'parziale' | 'annullato';
  total: string;         // totaleOrdine formattato
  shipping: string;      // modalitaSpedizione
  payment: string;       // condizionePagamento
  address: string;       // indirizzo formattato
}

// --- Documents ---

export interface LineeDocumento {
  seriale: string;       // numero documento
  riga: number;
  codart: string;
  um: string;
  qtaVenduta: number;
  prezzo: number;
  valoreRiga: number;
}

export interface Documento {
  // === Identificativi ===
  id: string;
  idDocumento: number;
  idCliente: number;
  dtkCodiceCliente?: string;

  // === Tipo documento ===
  tipoDocumento: string; // es. 'DDT', 'Fattura', 'Nota Credito'
  tipoDoc: string;       // codice breve (es. 'DDT', 'FAT', 'NC')
  serDoc?: string;       // serie (es. '001')
  annoDoc?: number;
  numeroDocumento: string; // es. '001/2026'

  // === Date & Importi ===
  dataDocumento: string;
  importoDocumento: number;
  totaleImponibile?: number;
  totaleIva?: number;
  speseSpedizione?: number;
  totSpTrasporto?: number;
  totSpImballaggio?: number;
  totSpIncasso?: number;

  // === Spedizione ===
  codiceCorriere?: string;
  nomeCorriere?: string;
  codiceTracking?: string;
  indirizzoCorriere?: string;
  cittaCorriere?: string;
  capCorriere?: string;
  provinciaCorriere?: string;

  // === Riferimenti ===
  idAgente?: number;
  nomeAgente?: string;
  idCondizionePagamento?: number;
  condizionePagamento?: string;
  idDocumentoEGM?: string; // ID nel gestionale

  // === File ===
  pdfUrl?: string;

  // === Righe ===
  righe?: LineeDocumento[];

  // === Campi UI legacy ===
  number: string;        // = numeroDocumento
  type: 'fattura' | 'ddt' | 'nota_credito';
  date: string;          // = dataDocumento
  amount: string;        // importo formattato
  orderId?: string;
}

// --- Agents ---

export interface AgenteFull {
  id: string;            // come stringa
  idAgente: number;      // IdAgente numerico
  dtkCodiceAgente: string;
  nome: string;
  cognome: string;
  nomeCompleto: string;
  email: string;
  telefono?: string;
  cellulare?: string;
  zona?: string;         // es. 'Centro-Nord', 'Centro-Sud'
}

// --- Users / Auth ---

/**
 * Ruolo granulare di un sotto-utente creato dal cliente B2B (collection
 * `operatori`). Asse ORTOGONALE a `UserProfile.ruolo`: al login ogni
 * operatore diventa `cliente_subordinato`, e questo dice *quale* dei tre
 * poteri ha. Mirror di `OperatoreRuolo` in `backend/auth/models.py`.
 */
export type OperatoreRuolo = 'vedi-sito' | 'crea-carrello' | 'crea-ordine';

export interface UserProfile {
  userId: string;        // GUID
  idCliente?: number;
  idAgente?: number;
  ruolo: 'admin' | 'agente' | 'cliente' | 'cliente_subordinato' | 'admin_operatore';
  nome: string;
  cognome: string;
  email: string;
  attivo: boolean;
  ultimaAttivita?: string;
  /** Solo per ruolo==='admin_operatore' — vedi ADMIN_PERMESSI backend. */
  permessiAdmin?: AdminPermission[];
  /**
   * Solo per ruolo==='cliente_subordinato'. `null`/assente per ogni altra
   * identità. Non usarlo direttamente per decidere cosa mostrare: passa da
   * `canAddToCart` / `canPlaceOrder` di `useAuth()`, così la regola resta
   * in un posto solo e allineata al backend.
   */
  ruoloOperatore?: OperatoreRuolo | null;
  /** userId di chi sta impersonando; in impersonazione non si ordina. */
  impersonatoDa?: string | null;
  /**
   * Account attivo senza Cliente su NTS (`idCliente` assente): il Cliente
   * nasce al primo ordine. Non leggerlo direttamente: passa da
   * `useContatto()` (`.scratch/contatti-lead/spec.md`).
   */
  contatto?: boolean;
}

export type AdminPermission = 'contenuti' | 'resi_ordini' | 'abbinatore' | 'segnalazioni' | 'catalogo' | 'clienti' | 'sistema' | 'carriere';

// --- CRM ---

export interface MacchinaCliente {
  id: string;
  idCliente: number;
  idMacchina: number;
  nomeMacchina: string;
  nomeMarca: string;
  tipoMacchina: string;
  matricola?: string;
  dataInstallazione?: string;
  note?: string;
}

// --- Preventivi ---

export interface LineePreventivo {
  sku: string;
  title: string;
  qty: number;
  prezzoUnitario: number;
  sconto: number;
  totaleLinea: number;
}

export interface Preventivo {
  id: string;
  idCliente: number;
  ragioneSocialeCliente: string;
  dtkCodiceCliente?: string;
  idAgente: number;
  nomeAgente: string;
  data: string;
  validoFino: string;
  idStatoPreventivo: number;
  stato: 'bozza' | 'inviato' | 'accettato' | 'rifiutato' | 'scaduto';
  note?: string;
  items: LineePreventivo[];
  imponibile: number;
  iva: number;
  totale: number;
  percentualeIva: number;
}

// --- Pagamenti / Partite Aperte ---

export interface PartitaAperta {
  id: string;
  idCliente: number;
  numeroDocumento: string;
  tipoDocumento: string;
  dataDocumento: string;
  dataScadenza: string;
  importo: number;
  importoPagato: number;
  residuo: number;
  stato: 'aperta' | 'scaduta' | 'pagata' | 'in_contestazione';
}

// --- RMA ---

export type RMAMotivo =
  | 'difetto_stampa'
  | 'prodotto_danneggiato'
  | 'qualita_non_conforme'
  | 'articolo_errato_ricevuto'
  | 'articolo_ordinato_erroneamente'
  | 'quantita_errata'
  | 'altro';

export interface RMAUnit {
  id: string;                          // es. 'CF259A-1', 'CF259A-2'
  sku: string;
  title: string;
  brand: string;
  category: string;
  prezzoPagato: number;
  motivo: RMAMotivo | '';
  descrizione: string;
  photoPath?: string;                  // chiave nel bucket (private/rma/uploads/…)
  photoUrl?: string;                   // URL presigned, aggiunto dalla risposta
  photoDownloadUrl?: string;           // idem, per scaricarlo
  photoName?: string;
  photoMime?: string;                  // 'image/jpeg' | 'image/png' | 'application/pdf'
  adminDecision: 'pending' | 'approvata' | 'respinta';
  adminNote: string;
  adminPhotoPath?: string;             // allegato caricato dall'operatore (es. a supporto del respingimento)
  adminPhotoUrl?: string;              // URL presigned, aggiunto dalla risposta
  adminPhotoDownloadUrl?: string;
  adminPhotoName?: string;
  adminPhotoMime?: string;
}

export interface RMAItem {
  sku: string;
  title: string;
  brand?: string;
  category?: string;
  qtyReturned: number;
  motivo?: string;                     // legacy
  prezzoPagato?: number;
  units?: RMAUnit[];                   // un elemento per ogni pezzo fisico
}

/** Chi spedisce la merce resa, dal valore dei pezzi richiesti IVA esclusa (INT-129). */
export interface SpedizioneReso {
  valore: number;
  soglia: number;
  aCuraDtk: boolean;
  testo: string;
}

export interface RMARequest {
  id: string;
  numero?: string;
  orderId: string;
  idOrdine?: number;
  numeroOrdine?: string;
  idCliente?: number;
  ragioneSocialeCliente?: string;
  date: string;
  status: 'aperta' | 'in_lavorazione' | 'chiusa';
  items: RMAItem[];
  reason?: string;                     // ora opzionale (la motivazione è per-unità)
  notes?: string;
  autorizzazioneRitiro?: boolean;
  corriereDiRitiro?: string;
  dataRitiro?: string;
  noteRitiro?: string;
  importoStimato?: number;
  spedizioneReso?: SpedizioneReso;
  documentoUrl?: string;               // Modulo RMA - Reso Garanzia in PDF (generato automaticamente)
  documentoEsitoUrl?: string;          // Esito della pratica in PDF, solo a RMA chiuso
  /** RMA creata sul gestionale NTS alla chiusura pratica (solo se ≥1 pezzo
   * approvato, push obbligatorio — se il gestionale rifiuta la chiamata la
   * pratica non viene chiusa) — numero progressivo, es. "RMA/2026/0012". */
  erpRma?: {
    tipork?: string;
    anno?: number;
    serie?: string;
    numdoc?: number;
    createdAt: string;
  } | null;
  /** Note di credito emesse su NTS che citano il numero dell'RMA nel campo
   * Riferimento, collegate dal job ``rma_note_credito`` (migration 075). */
  noteCredito?: NotaCreditoRMA[];
}

export interface NotaCreditoRMA {
  tipork: string;
  anno: number;
  serie: string;
  numdoc: number;
  numero: string;          // es. "IT/2026/00012"
  data: string | null;     // YYYY-MM-DD
  totale: number | null;
  riferimento: string;
  collegataAt: string;
}

/** L'Ordine RMA di una pratica chiusa, letto da NTS (INT-133): si evade
 *  nella nota di credito, quindi lo stato dice a che punto è il rimborso. */
export type StatoOrdineRMA = 'da_accreditare' | 'accreditato_in_parte' | 'accreditato' | 'annullato';

export interface OrdineRMA {
  rma: string;
  numero: string;              // es. "RMA/2026/00060"
  data: string | null;         // YYYY-MM-DD
  stato: StatoOrdineRMA;
  totale: number | null;       // IVA esclusa
  totaleIvato: number;
  righe: {
    sku: string;
    title: string;
    quantita: number;
    quantitaAccreditata: number;
    prezzoUnitario: number;
    totaleRiga: number;
    /** Stato della riga d'ordine (`erp/ordini.py::stato_riga`). */
    stato: 'aperta' | 'parziale' | 'evasa' | 'chiusa' | 'annullata';
  }[];
  noteCredito: NotaCreditoRMA[];
  aggiornatoAl: string | null;
}

/** "Nota di credito IT/2026/00012 del 02/10/2026 · 19,34 €" */
export function formatNotaCredito(n: NotaCreditoRMA): string {
  const data = n.data ? ` del ${n.data.split('-').reverse().join('/')}` : '';
  const totale = n.totale != null
    ? ` · ${n.totale.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}`
    : '';
  return `Nota di credito ${n.numero}${data}${totale}`;
}

/** Formatta la chiave gestionale di una RMA come "RMA/2026/0012". */
export function formatErpRmaNumber(erpRma?: RMARequest['erpRma']): string | null {
  if (!erpRma || !erpRma.numdoc) return null;
  return `${erpRma.serie}/${erpRma.anno}/${String(erpRma.numdoc).padStart(4, '0')}`;
}

// --- Site ---

export interface ProductCategory {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  count: string;
  features: string[];
}

export interface CompanyPageData {
  id: string;
  title: string;
  content: string;
}

export interface SupportPageData {
  id: string;
  title: string;
  content: string;
  downloads?: string[];
}

export interface ContactEmail {
  dept: string;
  email: string;
}

export interface ContactInfo {
  address: string;
  phone: string;
  fax: string;
  hours: string;
  emails: ContactEmail[];
  iban: string;
  bank: string;
}

export interface ValueProp {
  icon: string;
  title: string;
  desc: string;
}

export interface SearchItem {
  id: string;
  type: 'product' | 'brand' | 'category' | 'page' | 'model';
  title: string;
  subtitle: string;
  description: string;
  sku?: string;
  brand?: string;
  category?: string;
  url: string;
  keywords: string;
  modelId?: string;
}

// --- Notifications ---

export interface Notification {
  id: string;
  type: 'stock' | 'ordine' | 'promo' | 'sistema' | 'rma';
  title: string;
  message: string;
  date: string;
  read: boolean;
  link?: string;
}

// --- Account ---

export interface AccountInfo {
  balance: string;
  openItems: PartitaAperta[];
}

// --- Printer Models (Parco Macchine UI) ---

export interface PrinterModel {
  id: string;
  brand: string;
  model: string;
  type: string;
}
